// src/pages/DriverOfficialControlScreen.js (REPLACE ENTIRE FILE)
import React, { useEffect, useMemo, useState } from "react";
import { SafeAreaView, View, Text, StyleSheet, Pressable, ScrollView, Platform } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useTranslation } from "../i18n";

const isNumericId = (v) => /^\d+$/.test(String(v || "").trim());

const getPersonIdFromPathname = () => {
  if (Platform.OS !== "web") return null;
  try {
    const segs = (window.location?.pathname || "").split("/").filter(Boolean);
    const i = segs.findIndex((s) => String(s).toLowerCase() === "driver");
    const candidate = i !== -1 ? segs[i + 1] : null;
    return candidate && isNumericId(candidate) ? String(candidate) : null;
  } catch {
    return null;
  }
};

// ✅ treat as missing if empty / dash / "null"
const isMissing = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return true;
  if (s === "—" || s === "-") return true;
  if (s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return true;
  return false;
};

export default function DriverOfficialControlScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { t } = useTranslation();

  const personId = useMemo(() => {
    const p = route?.params?.personId ?? route?.params?.id ?? null;
    const fromParams = p != null ? String(p).trim() : null;
    if (fromParams && isNumericId(fromParams)) return fromParams;
    return getPersonIdFromPathname();
  }, [route?.params]);

  const EMPTY = useMemo(
    () => ({
      name: "—",
      license_number: "—",
      cin_number: "—",
      professional_card_number: null,
    }),
    []
  );

  const [data, setData] = useState(EMPTY);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!personId) return;

    let alive = true;
    setHasError(false);

   const url = `https://api.taxi-yaounde.com/api/drivers/person/${encodeURIComponent(personId)}`;

    fetch(url)
      .then((r) => r.json())
      .then((json) => {
        if (!alive) return;
        if (json?.success && json?.data) {
          setData({ ...EMPTY, ...json.data });
          setHasError(false);
        } else {
          setData(EMPTY);
          setHasError(true);
        }
      })
      .catch(() => {
        if (!alive) return;
        setData(EMPTY);
        setHasError(true);
      });

    return () => {
      alive = false;
    };
  }, [personId, EMPTY]);

  // ✅ fields
  const driverName = data?.name;
  const licence = data?.license_number;
  const cin = data?.cin_number;

  // ✅ capacity: if null => "Not Registered" (translated)
  const capNumber = useMemo(() => {
    const raw = data?.professional_card_number;
    const s = String(raw ?? "").trim();
    if (!s || s.toLowerCase() === "null" || s === "—") return t("driver.capacityNotRegistered");
    return s;
  }, [data?.professional_card_number, t]);

  // ✅ status always ABSENT(E) (translated)
  const capStatus = t("driver.capacityAbsent");

  return (
    <SafeAreaView style={styles.safe}>
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scroll}>
        <View style={styles.topRow}>
          <Pressable style={styles.backBtn} onPress={() => navigation.goBack()}>
            <Text style={styles.backIcon}>‹</Text>
            <Text style={styles.backText}>{t("common.back")}</Text>
          </Pressable>
        </View>

        {/* Card 1 */}
        <View style={styles.card}>
          <View style={[styles.header, styles.headerGreen]}>
            <Text style={styles.headerIcon}>🧑🏾‍✈️</Text>
            <Text style={styles.headerTitle}>
              {t("driver.profileTitle")}{" "}
              <Text style={styles.headerSub}>({t("driver.profileSub")})</Text>
            </Text>
          </View>

          <Row icon="👤" label={t("driver.name")} value={driverName} missingText={capStatus} />
          <Row icon="🚗" label={t("driver.permit")} value={licence} missingText={capStatus} />
          <Row icon="🪪" label={t("driver.cni")} value={cin} missingText={capStatus} isLast />
        </View>

        {/* Card 2 */}
        <View style={styles.card}>
          <View style={[styles.header, styles.headerRed]}>
            <Text style={styles.headerIcon}>⚠️</Text>
            <Text style={styles.headerTitle}>
              {t("driver.capacityTitle")}{" "}
              <Text style={styles.headerSub}>({t("driver.capacitySub")})</Text>
            </Text>
          </View>

          <Row icon="🧾" label={t("driver.capacityNumber")} value={capNumber} missingText={capStatus} />
          <Row
            icon="✅"
            label={t("driver.capacityStatus")}
            value={capStatus}
            isChip
            chipTone="danger"
            isLast
          />

          <View style={styles.helperWrap}>
            <Text style={styles.helperTextItalic}>{t("driver.capacityHelper")}</Text>
          </View>
        </View>

        {hasError ? <Text style={styles.backendHint}>{t("errors.backendNotReady")}</Text> : null}
        <View style={{ height: 18 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

function Row({ icon, label, value, missingText, isChip = false, chipTone = "neutral", isLast = false }) {
  const missing = !isChip && isMissing(value);

  return (
    <View style={[styles.row, isLast && styles.rowLast]}>
      <Text style={styles.rowIcon}>{icon}</Text>
      <Text style={styles.rowLabel}>{label} :</Text>

      {isChip ? (
        <View
          style={[
            styles.statusChip,
            chipTone === "danger" && styles.statusChipDanger,
            chipTone === "neutral" && styles.statusChipNeutral,
          ]}
        >
          <Text style={styles.statusChipText}>{value}</Text>
        </View>
      ) : missing ? (
        // ✅ missing value => red ABSENT(E) chip (instead of "-")
        <View style={[styles.statusChip, styles.statusChipDanger]}>
          <Text style={styles.statusChipText}>{missingText}</Text>
        </View>
      ) : (
        <Text style={styles.rowValue}>{String(value)}</Text>
      )}
    </View>
  );
}

const DARK_GREEN = "#0F3C2F";
const PAGE_BG = "#EEF5F4";
const HEADER_GREEN = "#DCEEDD";
const HEADER_RED = "#F4D6D6";
const MUTED_GREEN = "#4B6B57";
const TEXT_DARK = "#0B1F14";

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: PAGE_BG },
  scroll: { paddingHorizontal: 16, paddingTop: 14, paddingBottom: 22 },

  topRow: { width: "100%", flexDirection: "row", justifyContent: "flex-start", marginBottom: 10 },
  backBtn: {
    backgroundColor: DARK_GREEN,
    borderRadius: 26,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    shadowColor: "#000",
    shadowOpacity: 0.16,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 8 },
    elevation: 6,
  },
  backIcon: { color: "#fff", fontSize: 20, fontWeight: "900", marginTop: -1 },
  backText: { color: "#fff", fontSize: 18, fontWeight: "900" },

  card: {
    marginTop: 14,
    borderRadius: 22,
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 14 },
    elevation: 3,
  },

  header: { paddingVertical: 10, paddingHorizontal: 14, flexDirection: "row", alignItems: "center", gap: 10 },
  headerGreen: { backgroundColor: HEADER_GREEN },
  headerRed: { backgroundColor: HEADER_RED },
  headerIcon: { fontSize: 18, marginTop: 1 },
  headerTitle: { fontSize: 16, fontWeight: "900", color: "#1F6F3D" },
  headerSub: { fontSize: 15, fontWeight: "900", color: "#557B64" },

  row: {
    paddingHorizontal: 14,
    paddingVertical: 11,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(15,23,42,0.08)",
  },
  rowLast: { borderBottomWidth: 0 },

  rowIcon: { fontSize: 13, width: 22, textAlign: "center" },
  rowLabel: { flex: 1, fontSize: 12.6, fontWeight: "800", color: MUTED_GREEN, paddingRight: 8, flexWrap: "wrap" },
  rowValue: { maxWidth: "52%", fontSize: 12.2, fontWeight: "900", color: TEXT_DARK, textAlign: "right", flexWrap: "wrap" },

  statusChip: {
    maxWidth: "52%",
    flexShrink: 1,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 16,
    alignSelf: "flex-end",
    elevation: 2,
  },
  statusChipDanger: { backgroundColor: "#DC2626" },
  statusChipNeutral: { backgroundColor: "#6B7280" },
  statusChipText: { color: "#FFFFFF", fontSize: 12.2, fontWeight: "900", textAlign: "center" },

  helperWrap: { paddingHorizontal: 14, paddingVertical: 10 },
  helperTextItalic: { color: "rgba(17,24,39,0.72)", fontWeight: "700", fontStyle: "italic", fontSize: 12.5, lineHeight: 16 },

  backendHint: { marginTop: 10, fontSize: 12, color: "rgba(17,24,39,0.55)", fontWeight: "700", textAlign: "center", fontStyle: "italic" },
});
